import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isOperator } from '../utils/permission';
import Loading from '../components/Loading';
import { countFromSwex } from '../utils/swexRoster';
import { ATTRS, Cell, buildCells, mobImgUrl } from '../utils/monsterCells';
import { loadMonsterMapping } from '../utils/monsterMapping';
import { useBackHandler } from '../hooks/useBackHandler';

/**
 * 보유몹 — 누가 어떤 몹을 가졌나.
 *
 * 점령전 방덱을 짤 때 "이 덱을 만들 수 있는 사람"을 알아야 한다. 게임 API 는 남의 보유
 * 몹을 주지 않으므로 **각자 자기 것을 켜 둔다.**
 *
 * **태생 4~5성만** 다룬다. 그 아래는 점령전 덱에 쓰지 않아서 체크할 이유가 없다
 * (1,140칸 → 839칸, 콜라보 짝을 한 칸으로 묶으면 728칸).
 *
 * 화면이 하는 일은 둘이다.
 *   - 내 보유몹 켜고 끄기 (누르면 바로 저장한다)
 *   - 칸마다 몇 명이 가졌는지 보고, 누르면 그게 누구인지 보기
 *
 * 데스크톱·모바일을 나누지 않았다. 격자는 원래 폭에 따라 접히는 모양이라 `isMobile` 로
 * 크기만 달리하면 충분하다(휴가 화면과 같은 방식).
 *
 * 칸을 만드는 규칙(콜라보 짝을 어느 이름으로 모으는지, 태생 성급, 그림)은
 * `utils/monsterCells` 에 있다 — 방덱 화면이 같은 열쇠를 써야 하기 때문이다.
 */

interface Member {
    uuid: string;
    nickname: string;
    profileImg: string | null;
    /** 전체 개수 */
    count: number;
    /** (속성, 태생 성급) 별 개수 — `{ Water: { '5': 12 } }`. 지금 보고 있는 현황만 세어 보여준다 */
    byView?: Record<string, Record<string, number>>;
}

const Roster: React.FC<{ isMobile: boolean; user?: any }> = ({ isMobile, user }) => {
    /**
     * 남의 보유몹을 대신 체크할 수 있는지 — **선임 이상**(마스터·부마스터·선임) 또는
     * 사이트 운영자. 화면에서 막는 것은 편의일 뿐이고 실제 차단은 서버가 한다
     * (`roster.ts` 의 `canOperate`).
     */
    const canEditOthers = isOperator(user);
    const [mapping, setMapping] = useState<any>(null);
    /** 내가 가진 개수 `{ 열쇠: 마리수 }`. null 이면 아직 못 받았다 */
    const [me, setMe] = useState<Record<string, number> | null>(null);
    /** 칸마다 길드 전체 합계. [전체] 를 볼 때 쓴다 */
    const [totals, setTotals] = useState<Record<string, number>>({});
    const [members, setMembers] = useState<Member[]>([]);
    const [myUuid, setMyUuid] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const [attr, setAttr] = useState<string>('Water');
    /** 태생 성급. **기본은 5성** — 점령전 덱에 먼저 보는 쪽이다 */
    const [stars, setStars] = useState<4 | 5>(5);
    const [search, setSearch] = useState('');
    /**
     * 길드원 알약 목록을 펼쳤나.
     *
     * 폰에서는 서른 명이 다섯 줄을 차지해 격자가 화면 밖으로 밀렸다. **접으면 첫 줄만**
     * 보이고(전체·나·몇 명), 오른쪽 화살표로 펼친다. 데스크톱은 한두 줄이라 늘 펼쳐 둔다.
     */
    const [membersOpen, setMembersOpen] = useState(false);
    /**
     * 누구 것을 보고 있나.
     *   null      내 것 (내 알약이 목록 맨 앞에 있다)
     *   'all'     길드 전체 합계 — 보기 전용
     *   Member    그 사람 것
     */
    const [viewing, setViewing] = useState<Member | 'all' | null>(null);
    const [viewingCounts, setViewingCounts] = useState<Record<string, number>>({});
    /** 남의 목록을 실제로 받아 왔나 (받기 전에 고치면 덮어쓸 수 있다) */
    const [viewingLoaded, setViewingLoaded] = useState(false);
    /** 눌러서 열어 둔 칸의 보유자 */
    const [owners, setOwners] = useState<
        { key: string; label: string; total: number; owners: (Member & { n?: number })[]; missing: Member[] } | null
    >(null);
    /**
     * 개수를 **클릭 시점에 바로 읽고 쓰는** 사본.
     *
     * 화면 상태(`me`)만 보면 빠르게 여러 번 누를 때 두 번째 클릭이 첫 번째 결과를 못 본다
     * (그 사이 다시 그려지지 않았으므로 클로저에 옛 값이 남는다). 그래서 개수 계산은 이
     * 사본으로 하고 화면이 그걸 따라간다.
     */
    const countRef = useRef<Record<string, number>>({});
    /** 칸마다 예약된 저장. 연달아 누르면 **마지막 개수 한 번만** 보낸다 */
    const saveTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
    /** 아직 안 보낸 저장 (화면을 떠날 때 밀어 넣는다) */
    const pendingSave = useRef<Map<string, number>>(new Map());
    /**
     * 길게 누르기.
     *
     * 폰에는 마우스 오른쪽이 없다. 안드로이드 크롬은 길게 누르면 `contextmenu` 를 주기도
     * 하지만 기기·브라우저마다 다르므로 **직접 시간을 잰다.** 길게 눌러 명단이 열렸으면
     * 손을 뗄 때 오는 클릭은 무시한다 — 안 그러면 칸이 같이 켜진다.
     */
    const longTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const longFired = useRef(false);
    /**
     * 마지막으로 실패한 이유.
     *
     * 예전에는 저장이 실패하면 조용히 되돌렸다 — 화면에서는 "눌렀는데 안 켜진다"로만
     * 보이고 왜 그런지 알 길이 없었다. 이유를 그대로 띄운다.
     */
    const [problem, setProblem] = useState<string | null>(null);

    useEffect(() => {
          loadMonsterMapping()
            .then(setMapping)
            .catch(() => {});
    }, []);

    /** 한 번이라도 저장한 적이 있나 (처음 온 사람에게만 기본값을 채운다) */
    const [hasRecord, setHasRecord] = useState<boolean | null>(null);

    const load = useCallback(async () => {
        try {
            const r = await fetch('/api/roster', { credentials: 'include' });
            if (!r.ok) {
                setProblem(`목록을 못 받았습니다 (HTTP ${r.status})`);
            } else {
                const d = await r.json();
                setMe(d.me || {});
                setHasRecord(d.hasRecord === true);
                setTotals(d.totals || {});
                setMembers(d.members || []);
                setMyUuid(d.myUuid || null);
            }
        } catch (e: any) {
            setProblem(`목록을 못 받았습니다 (${e?.message || '연결 실패'})`);
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    /** 태생 4~5성 칸 전부 (콜라보 짝은 한 칸으로) */
    const allCells = useMemo<Cell[]>(() => buildCells(mapping), [mapping]);

    /** 이름으로 찾고 있나 — 그동안은 속성·성급을 따지지 않는다 */
    const searching = search.trim().length > 0;

    /** 알약 순서: **내가 맨 앞**, 그다음은 서버가 준 순서(칸 수 많은 사람부터) */
    const orderedMembers = useMemo(() => {
        if (!myUuid) return members;
        const mine = members.filter((m) => m.uuid === myUuid);
        return [...mine, ...members.filter((m) => m.uuid !== myUuid)];
    }, [members, myUuid]);

    /**
     * 처음 왔을 때 켜 둘 칸 — **태생 5성 빛·암만 빼고 전부.**
     *
     * 대부분의 몹은 다 갖고 있어서, 빈 판에서 700번을 누르게 하는 것은 말이 안 된다.
     * 태생 5성 빛·암은 귀해서 대개 없으므로 그것만 꺼 둔 상태로 시작한다 — 사람은
     * **없는 것만** 끄면 된다.
     */
    const defaultKeys = useMemo(
        () => Array.from(new Set(
            allCells
                .filter((c) => !(c.natural === 5 && (c.attribute === 'Light' || c.attribute === 'Dark')))
                .map((c) => c.key)
        )),
        [allCells]
    );

    /**
     * 개수를 통째로 저장한다. SWEX 파일로 채울 때 쓴다.
     *
     * `uuid` 를 주면 그 사람 것을 채운다(선임 이상 — 서버가 막는다).
     */
    const putCounts = useCallback(async (counts: Record<string, number>, uuid?: string) => {
        const r = await fetch('/api/roster', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(uuid ? { counts, uuid } : { counts })
        });
        if (!r.ok) {
            let why = `HTTP ${r.status}`;
            try { why = (await r.json())?.error || why; } catch { /* 코드만 */ }
            throw new Error(why);
        }
        countRef.current = { ...counts };
        if (uuid) setViewingCounts(counts); else setMe(counts);
        setHasRecord(true);
        await load();   // 길드 합계를 다시 받는다
    }, [load]);

    /**
     * SWEX 로 뽑은 계정 JSON 을 읽어 보유몹을 채운다.
     *
     * 파일은 **브라우저에서만** 읽는다 — 7MB 를 서버로 올릴 이유가 없고, 계정 정보(룬·아이템·
     * 우편까지 들어 있다)를 서버에 남길 이유도 없다. 세어 놓은 개수만 보낸다.
     */
    const [importing, setImporting] = useState(false);
    const fileInput = useRef<HTMLInputElement | null>(null);

    async function importSwex(file: File) {
        if (!/\.json$/i.test(file.name)) {
            setProblem(`${file.name} 은 JSON 파일이 아닙니다 — SWEX 가 저장한 "계정이름-번호.json" 을 고르세요.`);
            if (fileInput.current) fileInput.current.value = '';
            return;
        }
        if (!mapping) { setProblem('몹 표를 아직 못 받았습니다 — 잠시 뒤에 다시 해 주세요.'); return; }
        setImporting(true);
        setProblem(null);
        try {
            const account = JSON.parse(await file.text());
            const r = countFromSwex(account, mapping);

            // 물어보는 것 하나만 남긴다 — 셈 내역(봉인소·태생·매핑 누락)은 보여 주지 않는다
            if (!window.confirm('지금까지 설정해둔 현황은 덮어써집니다. 계속할까요?')) return;
            await putCounts(r.counts, viewingMember?.uuid);
        } catch (e: any) {
            setProblem(`SWEX 파일을 읽지 못했습니다 — ${e?.message || '알 수 없는 오류'}`);
        } finally {
            setImporting(false);
            if (fileInput.current) fileInput.current.value = '';   // 같은 파일을 다시 고를 수 있게
        }
    }

    /** 목록을 통째로 저장한다 (기본값 채우기 — 전부 한 마리로) */
    const saveAll = useCallback(async (keys: string[]) => {
        const counts: Record<string, number> = {};
        for (const k of keys) counts[k] = 1;
        try {
            const r = await fetch('/api/roster', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ counts })
            });
            if (!r.ok) {
                let why = `HTTP ${r.status}`;
                try { why = (await r.json())?.error || why; } catch { /* 코드만 */ }
                throw new Error(why);
            }
            countRef.current = { ...counts };
            setMe(counts);
            setHasRecord(true);
            await load();   // 길드 합계를 다시 받는다
            return true;
        } catch (e: any) {
            setProblem(`기본값을 채우지 못했습니다 — ${e?.message || '연결 실패'}`);
            return false;
        }
    }, [load]);

    /**
     * 처음 온 사람에게 기본값을 채운다.
     *
     * 저장한 적이 **한 번도 없을 때만** 한다(`hasRecord`). 빈 목록만 보고 판단하면, 다
     * 끄고 나간 사람이 다시 들어올 때마다 전부 켜져 버린다.
     */
    useEffect(() => {
        if (hasRecord !== false || me === null || viewing) return;
        if (!defaultKeys.length) return;
        saveAll(defaultKeys);
    }, [hasRecord, me, viewing, defaultKeys, saveAll]);

    /** 화면에 그릴 칸 */
    const cells = useMemo(() => {
        const q = search.trim().toLowerCase();
        return allCells
            .filter((c) => {
                // 검색 중에는 속성·성급을 따지지 않는다 — 이름으로 찾을 때는 그 몹이 몇 성인지
                // 어느 속성인지 모르고 찾는 경우가 많다
                if (q) {
                    const info = mapping?.monsters?.[c.family];
                    const aliases: string[] = info?.[c.attribute]?.aliases || [];
                    return c.label.toLowerCase().includes(q)
                        || c.family.toLowerCase().includes(q)
                        || aliases.some((al) => al.toLowerCase().includes(q));
                }
                return c.attribute === attr && c.natural === stars;
            })
            .sort((x, y) => y.natural - x.natural || x.label.localeCompare(y.label));
    }, [allCells, attr, stars, search, mapping]);

    /** 길드 전체 합계를 보고 있나 — 보기 전용이고, 한 번 누르면 누가 가졌는지 보여준다 */
    const viewingAll = viewing === 'all';
    /** 남의 것을 보고 있으면 그 사람 */
    const viewingMember = viewing && viewing !== 'all' ? viewing : null;

    /**
     * 지금 보고 있는 것을 고칠 수 있나.
     *
     * 내 것은 언제나, 남의 것은 **선임 이상**만. [전체] 는 합계라서 고칠 수 없다.
     * 남의 것을 아직 못 받았으면 손대지 않는다 — 빈 상태에서 세면 그 사람 것을 덮어쓴다.
     */
    const editable = viewingAll
        ? false
        : viewingMember
            ? (canEditOthers && viewingLoaded)
            : me !== null;

    /** 화면에 보여줄 개수 — 내 것 / 남의 것 / 길드 합계 */
    const shownCounts = useMemo<Record<string, number>>(
        () => (viewingAll ? totals : viewingMember ? viewingCounts : (me || {})),
        [viewingAll, totals, viewingMember, viewingCounts, me]
    );

    // 클릭 시점에 읽는 사본을 **지금 보고 있는 것**과 맞춘다
    useEffect(() => {
        countRef.current = { ...(viewingAll ? {} : viewingMember ? viewingCounts : (me || {})) };
    }, [viewingAll, viewingMember, viewingCounts, me]);

    /**
     * 화면을 떠날 때 **아직 안 보낸 저장을 밀어 넣는다.**
     *
     * 마지막 클릭 뒤 220ms 안에 탭을 옮기면 그 한 번이 사라진다 — 사람은 저장된 줄 안다.
     */
    useEffect(() => () => {
        // `for...of` 로 Map 을 돌리면 이 프로젝트의 컴파일 대상(ES5)에서 막힌다 — forEach 를 쓴다
        saveTimers.current.forEach((t) => clearTimeout(t));
        saveTimers.current.clear();
        pendingSave.current.forEach((n, key) => {
            fetch('/api/roster/count', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ key, n }),
                keepalive: true
            }).catch(() => {});
        });
        pendingSave.current.clear();
    }, []);

    /**
     * 서버에 실제로 보낸다. 실패하면 되돌리고 이유를 남긴다.
     *
     * `uuid` 를 주면 그 사람의 목록을 고친다(선임 이상만 — 서버가 막는다).
     */
    const sendSave = useCallback(async (key: string, n: number, uuid?: string) => {
        pendingSave.current.delete(key);
        try {
            const r = await fetch('/api/roster/count', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(uuid ? { key, n, uuid } : { key, n })
            });
            if (!r.ok) {
                let why = `HTTP ${r.status}`;
                try { why = (await r.json())?.error || why; } catch { /* 본문이 JSON 이 아니면 코드만 */ }
                throw new Error(why);
            }
        } catch (e: any) {
            console.warn('[보유몹] 저장 실패:', key, e?.message);
            setProblem(`저장하지 못했습니다 — ${e?.message || '연결 실패'} (새로고침하면 서버 값으로 돌아갑니다)`);
        }
    }, []);

    /**
     * 칸의 개수를 정한다. `delta` 를 주면 그만큼 더하고, `set` 을 주면 그 값으로 맞춘다.
     *
     * **화면은 누른 즉시 바뀌고**, 서버로는 잠깐(220ms) 기다렸다가 **마지막 값 한 번만**
     * 보낸다. 그래서 빠르게 여러 번 눌러도 씹히지 않고, 왕복은 한 번이다.
     */
    function bump(cell: Cell, opts: { delta?: number; set?: number }) {
        if (viewingAll) { openOwners(cell); return; }
        if (viewingMember) {
            if (!canEditOthers) { setProblem('남의 보유몹은 선임 이상만 고칠 수 있습니다.'); return; }
            if (!viewingLoaded) { setProblem(`${viewingMember.nickname} 님의 것을 아직 못 받았습니다.`); return; }
        } else if (me === null) {
            // 로그인 안 한 사람은 여기까지 오지 못한다(사이트가 로그인 화면을 먼저 띄운다).
            // 그래서 `me` 가 없다는 건 아직 못 받았다는 뜻이다
            setProblem('내 보유몹을 아직 못 받았습니다 — 새로고침해 주세요.');
            return;
        }

        const cur = countRef.current[cell.key] || 0;
        const want = opts.set !== undefined ? opts.set : cur + (opts.delta || 0);
        const n = Math.max(0, Math.min(99, want));
        if (n === cur) return;

        if (n === 0) delete countRef.current[cell.key]; else countRef.current[cell.key] = n;
        const next = { ...countRef.current };
        if (viewingMember) setViewingCounts(next); else setMe(next);
        // 길드 합계도 그만큼 따라 움직인다(내가 세는 중에도 [전체] 숫자가 맞게 보이도록)
        setTotals((prev) => {
            const v = Math.max(0, (prev[cell.key] || 0) + (n - cur));
            const copy = { ...prev };
            if (v === 0) delete copy[cell.key]; else copy[cell.key] = v;
            return copy;
        });
        if (problem) setProblem(null);

        const uuid = viewingMember?.uuid;
        const t = saveTimers.current.get(cell.key);
        if (t) clearTimeout(t);
        pendingSave.current.set(cell.key, n);
        saveTimers.current.set(cell.key, setTimeout(() => {
            saveTimers.current.delete(cell.key);
            sendSave(cell.key, n, uuid);
        }, 220));
    }

    /**
     * 칸을 눌렀을 때.
     *
     * 고칠 수 있으면 **한 마리 늘린다**(꾹 누르면 0으로 되돌린다). [전체] 를 보고 있거나
     * 남의 것을 못 고치는 사람이면 **누가 가졌는지** 보여준다.
     */
    function clickTile(cell: Cell) {
        if (editable) { bump(cell, { delta: 1 }); return; }
        openOwners(cell);
    }

    /**
     * 꾹 누르기.
     *
     * 고칠 수 있으면 **0으로 초기화**, 아니면 보유자 명단을 연다. 폰에는 마우스 오른쪽이
     * 없어서 시간을 직접 잰다(450ms).
     */
    function fireLongPress(cell: Cell) {
        if (editable) bump(cell, { set: 0 });
        else openOwners(cell);
    }

    function startLongPress(cell: Cell) {
        longFired.current = false;
        if (longTimer.current) clearTimeout(longTimer.current);
        longTimer.current = setTimeout(() => {
            longTimer.current = null;
            longFired.current = true;
            fireLongPress(cell);
        }, 450);
    }

    function endLongPress() {
        if (longTimer.current) { clearTimeout(longTimer.current); longTimer.current = null; }
    }

    async function openOwners(cell: Cell) {
        setOwners({ key: cell.key, label: cell.label, total: 0, owners: [], missing: [] });
        try {
            const r = await fetch(`/api/roster/owners?key=${encodeURIComponent(cell.key)}`, { credentials: 'include' });
            if (r.ok) {
                const d = await r.json();
                setOwners({
                    key: cell.key, label: cell.label,
                    total: d.total || 0, owners: d.owners || [], missing: d.missing || []
                });
            }
        } catch {
            /* 못 받으면 빈 목록 */
        }
    }

    /** 누구 것을 볼지 고른다. `null` = 내 것, `'all'` = 길드 합계 */
    async function viewMember(m: Member | 'all' | null) {
        setViewing(m);
        setViewingCounts({});
        setViewingLoaded(false);
        setProblem(null);
        if (!m || m === 'all') return;
        try {
            const r = await fetch(`/api/roster/of/${encodeURIComponent(m.uuid)}`, { credentials: 'include' });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            setViewingCounts((await r.json()).counts || {});
            setViewingLoaded(true);
        } catch (e: any) {
            setProblem(`${m.nickname} 님의 것을 못 받았습니다 (${e?.message || '연결 실패'})`);
        }
    }

    // 뒤로 가기: 보유자 팝업을 닫는다
    useBackHandler(!!owners, () => setOwners(null));

    const tile = isMobile ? 'clamp(40px, 11vw, 48px)' : 56;
    const gap = isMobile ? 'clamp(4px, 1vw, 6px)' : 8;

    return (
        <div style={{
            width: '100%',
            minHeight: 'calc(100vh - 60px)',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            paddingBottom: 'clamp(32px, 6vw, 40px)'
        }}>
            {/* 다른 탭과 같은 폭으로 가둔다(1200px) — 안 그러면 데스크톱에서 화면 끝까지 늘어난다 */}
            <div style={{
                maxWidth: 1200,
                margin: '0 auto',
                padding: 'clamp(16px, 3vw, 20px) clamp(12px, 2.5vw, 16px)'
            }}>
                {/* 머리말 */}
                <div style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 'clamp(12px, 2.5vw, 16px)',
                    padding: 'clamp(16px, 3vw, 20px) clamp(12px, 2.5vw, 16px)',
                    marginBottom: 'clamp(12px, 2.5vw, 16px)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                    textAlign: 'center'
                }}>
                    <h1 style={{
                        margin: 0,
                        marginBottom: 6,
                        fontSize: isMobile ? 'clamp(22px, 5vw, 26px)' : 34,
                        fontWeight: 900,
                        letterSpacing: '-0.5px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                    }}>보유몹</h1>
                    {/* 남의 것을 볼 때는 아무 말도 하지 않는다 — 위쪽 알약이 누구 것인지 보여 준다 */}
                    {!viewing && (
                        <p style={{ margin: 0, color: '#999', fontSize: isMobile ? 'clamp(10px, 2vw, 12px)' : 13, fontWeight: 500 }}>
                            컬렉션을 업데이트해주세요. 방덱 세팅에 활용됩니다.
                        </p>
                    )}
                    {viewingAll && (
                        <p style={{ margin: 0, color: '#999', fontSize: isMobile ? 'clamp(10px, 2vw, 12px)' : 13, fontWeight: 500 }}>
                            길드 전체가 가진 마리 수입니다. 몹을 클릭하면 보유 현황을 확인할 수 있습니다.
                        </p>
                    )}

                    {/*
                      SWEX 로 뽑은 계정 파일로 한 번에 채운다.
                      파일은 브라우저에서만 읽고 세어 놓은 개수만 서버로 보낸다 — 7MB 짜리
                      계정 정보(룬·아이템·우편까지 들어 있다)를 서버에 올릴 이유가 없다.
                    */}
                    {editable && (
                        // 머리말 카드는 가운데 정렬이라 여기서만 오른쪽으로 되돌린다
                        <div style={{ marginTop: 10, textAlign: 'right' }}>
                            <input
                                ref={fileInput}
                                type="file"
                                /* MIME 을 앞에 — 안드로이드 크롬이 첫 항목만 보는 때가 있다 */
                                accept="application/json,.json"
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) importSwex(f);
                                }}
                            />
                            <button
                                onClick={() => fileInput.current?.click()}
                                disabled={importing}
                                // 어떤 파일을 고르는지는 라벨을 없애고 여기로 옮겼다
                                title="SWEX 가 저장한 계정이름-번호.json"
                                style={{
                                    padding: isMobile ? '6px 12px' : '7px 16px',
                                    borderRadius: 999,
                                    border: '1px solid rgba(102, 126, 234, 0.35)',
                                    background: importing ? '#f1f3f9' : '#fff',
                                    color: '#667eea',
                                    fontSize: isMobile ? 11 : 12,
                                    fontWeight: 700,
                                    cursor: importing ? 'default' : 'pointer'
                                }}
                            >
                                {importing ? '읽는 중…' : 'json 으로 업데이트'}
                            </button>
                        </div>
                    )}
                </div>

                {/* 누구 것을 보나 */}
                <div style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 'clamp(12px, 2.5vw, 16px)',
                    padding: 'clamp(10px, 2vw, 12px)',
                    marginBottom: 'clamp(12px, 2.5vw, 16px)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
                }}>
                    {/*
                      숫자는 **지금 보고 있는 현황**(속성 + 태생 성급) 기준이다. 전체 개수를
                      적으면 "물 5성을 보는데 숫자는 전체"가 되어 견줄 수가 없다.
                      이름으로 검색하는 동안은 속성·성급을 안 따지므로 전체 개수를 적는다.
                    */}
                    <div style={{ position: 'relative' }}>
                    <div style={{
                        display: 'flex',
                        gap: 6,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        // 접으면 첫 줄만. 알약 한 줄이 26px 이다(글자 11px + 위아래 여백)
                        maxHeight: (isMobile && !membersOpen) ? 26 : undefined,
                        overflow: (isMobile && !membersOpen) ? 'hidden' : undefined,
                        // 접기 화살표가 알약과 겹치지 않게 오른쪽을 비워 둔다
                        paddingRight: isMobile ? 30 : 0
                    }}>
                        {/* 맨 앞은 [전체] — 길드가 통째로 몇 마리 가졌나 */}
                        <button onClick={() => viewMember('all')} style={pill(viewingAll, isMobile)}>
                            전체 {searching
                                ? Object.keys(totals).length
                                : cells.filter((c) => (totals[c.key] || 0) > 0).length}
                        </button>
                        {/*
                          그다음이 나다. 예전에는 [내 보유몹] 이라는 단추를 따로 뒀는데, 같은
                          성격인 것을 두 곳에 두면 지금 누구 것을 보는지 헷갈린다. 이름은 남들과
                          똑같이 적고, **맨 앞자리와 테두리**로만 내 것임을 알린다.
                        */}
                        {orderedMembers.map((m) => {
                            const mine = m.uuid === myUuid;
                            const n = searching ? m.count : (m.byView?.[attr]?.[String(stars)] || 0);
                            return (
                                <button
                                    key={m.uuid}
                                    onClick={() => viewMember(mine ? null : m)}
                                    style={{
                                        ...pill(mine ? (!viewing) : (viewingMember?.uuid === m.uuid), isMobile),
                                        ...(mine ? { outline: '2px solid rgba(102, 126, 234, 0.35)' } : null)
                                    }}
                                >
                                    {m.nickname} {n}
                                </button>
                            );
                        })}
                    </div>
                    {/* 접기·펼치기 — 폰에서만. 접혀 있으면 몇 명이 숨어 있는지 알려준다 */}
                    {isMobile && orderedMembers.length > 2 && (
                        <button
                            onClick={() => setMembersOpen((v) => !v)}
                            aria-label={membersOpen ? '접기' : '펼치기'}
                            style={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                width: 26,
                                height: 26,
                                borderRadius: 999,
                                border: '1px solid rgba(102, 126, 234, 0.3)',
                                background: '#fff',
                                color: '#667eea',
                                fontSize: 11,
                                fontWeight: 800,
                                lineHeight: 1,
                                cursor: 'pointer',
                                padding: 0
                            }}
                        >
                            {membersOpen ? '▲' : '▼'}
                        </button>
                    )}
                    </div>
                </div>

                {/* 속성·성급·검색 */}
                <div style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 'clamp(12px, 2.5vw, 16px)',
                    padding: 'clamp(10px, 2vw, 14px)',
                    marginBottom: 'clamp(12px, 2.5vw, 16px)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 8,
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {ATTRS.map((a) => (
                        <button
                            key={a.key}
                            onClick={() => { setAttr(a.key); setSearch(''); }}
                            title={a.label}
                            style={{
                                width: isMobile ? 34 : 40,
                                height: isMobile ? 34 : 40,
                                borderRadius: '50%',
                                border: attr === a.key && !search ? '2px solid #667eea' : '2px solid #e3f0ff',
                                background: attr === a.key && !search ? 'rgba(102, 126, 234, 0.12)' : '#fff',
                                boxShadow: attr === a.key && !search ? '0 2px 8px rgba(102, 126, 234, 0.35)' : 'none',
                                cursor: 'pointer',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <img loading="lazy" src={a.img} alt={a.label} style={{ width: isMobile ? 20 : 24, height: isMobile ? 20 : 24 }} />
                        </button>
                    ))}
                    <div style={{ width: 1, height: 24, background: 'rgba(102, 126, 234, 0.25)' }} />
                    {([[5, '5성'], [4, '4성']] as const).map(([v, label]) => (
                        <button key={label} onClick={() => setStars(v)} style={pill(stars === v, isMobile)}>
                            {label}
                        </button>
                    ))}
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="이름·별칭 검색 (속성 무시)"
                        style={{
                            flex: '1 1 140px',
                            minWidth: 120,
                            padding: '6px 10px',
                            border: '2px solid #e3f0ff',
                            borderRadius: 8,
                            fontSize: isMobile ? 12 : 13,
                            outline: 'none'
                        }}
                    />
                </div>

                {/* 격자 */}
                <div style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 'clamp(12px, 2.5vw, 16px)',
                    padding: 'clamp(12px, 2.5vw, 16px)',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)'
                }}>
                    {/* 무엇이 잘못됐는지 그대로 띄운다 */}
                    {problem && (
                        <div style={{
                            marginBottom: 12,
                            padding: '8px 12px',
                            borderRadius: 8,
                            background: 'rgba(224, 49, 49, 0.08)',
                            border: '1px solid rgba(224, 49, 49, 0.35)',
                            color: '#c92a2a',
                            fontSize: isMobile ? 11 : 12,
                            fontWeight: 600,
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8
                        }}>
                            <span>{problem}</span>
                            <button onClick={() => setProblem(null)} style={{ border: 'none', background: 'none', color: '#c92a2a', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}>×</button>
                        </div>
                    )}
                    {loading || !mapping ? (
                        <Loading />
                    ) : cells.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>해당하는 몹이 없습니다</div>
                    ) : (
                        <>
                            {/* 칸 수·켜진 수는 적지 않는다 — 위쪽 알약의 숫자가 같은 값을 말해 준다 */}
                            {!editable && viewing && (
                                <div style={{ fontSize: isMobile ? 11 : 12, color: '#888', marginBottom: 10, textAlign: 'left' }}>
                                    보기 전용
                                </div>
                            )}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(auto-fill, minmax(${typeof tile === 'number' ? `${tile}px` : tile}, 1fr))`,
                                gap
                            }}>
                                {cells.map((c) => {
                                    const n = shownCounts[c.key] || 0;
                                    const on = n > 0;
                                    return (
                                        <div
                                            key={c.uid}
                                            onClick={() => {
                                                // 꾹 눌러 처리했으면 손 뗄 때의 클릭은 흘린다
                                                if (longFired.current) { longFired.current = false; return; }
                                                clickTile(c);
                                            }}
                                            onContextMenu={(e) => { e.preventDefault(); if (!longFired.current) fireLongPress(c); }}
                                            onTouchStart={() => startLongPress(c)}
                                            onTouchEnd={endLongPress}
                                            onTouchMove={endLongPress}
                                            onTouchCancel={endLongPress}
                                            title={viewingAll
                                                ? `${c.label} (태생 ${c.natural}성) · 길드 전체 ${n}마리`
                                                : `${c.label} (태생 ${c.natural}성) · ${n}마리`}
                                            style={{
                                                position: 'relative',
                                                aspectRatio: '1 / 1',
                                                borderRadius: 8,
                                                border: on ? '2px solid #667eea' : '2px solid #eee',
                                                background: on ? 'rgba(102, 126, 234, 0.10)' : '#fafafa',
                                                boxShadow: on ? '0 2px 8px rgba(102, 126, 234, 0.3)' : 'none',
                                                cursor: 'pointer',
                                                overflow: 'hidden',
                                                transition: 'all 0.15s ease'
                                            }}
                                        >
                                            <img loading="lazy"
                                                src={mobImgUrl(c.imgPath)}
                                                alt={c.label}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'contain',
                                                    /**
                                                     * 안 가진 몹은 **색을 거의 남기고 어둡게만** 한다 — 게임의 몬스터
                                                     * 보관함과 같은 방식이다. 예전에는 완전 흑백에 반투명(`opacity: .45`)
                                                     * 이라 흰 카드 위에서 형체가 거의 사라져 무슨 몹인지 못 알아봤다.
                                                     *
                                                     * 켜진 칸은 그림 말고도 **보라 테두리·바탕색·그림자**로 구분되므로,
                                                     * 그림에서까지 색을 뺄 이유가 없다.
                                                     */
                                                    filter: on ? 'none' : 'grayscale(30%) brightness(0.6)'
                                                }}
                                            />
                                            {/*
                                              **두 마리 이상일 때만 숫자를 적는다.**
                                              한 마리는 밝은 칸 자체가 말해 주므로 숫자가 없어야 그림이 잘 보인다.
                                              [전체] 를 볼 때는 길드 합계라 1도 적는다 — 한 명만 가진 몹을
                                              구별해야 하기 때문이다.
                                            */}
                                            {(viewingAll ? n > 0 : n > 1) && (
                                                <span style={{
                                                    position: 'absolute',
                                                    right: 1,
                                                    bottom: 1,
                                                    minWidth: 15,
                                                    padding: '0 3px',
                                                    borderRadius: 7,
                                                    background: viewingAll ? 'rgba(60, 70, 100, 0.85)' : 'rgba(102, 126, 234, 0.95)',
                                                    color: '#fff',
                                                    fontSize: 10,
                                                    fontWeight: 800,
                                                    lineHeight: '15px',
                                                    textAlign: 'center'
                                                }}>{n}</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <div style={{ fontSize: isMobile ? 10 : 11, color: '#aaa', marginTop: 10, textAlign: 'left' }}>
                                {editable
                                    ? (isMobile
                                        ? '꾹 누르면 초기화됩니다.'
                                        : '꾹 누르거나 우클릭하면 초기화됩니다.')
                                    : '몹을 클릭하면 보유 현황을 확인할 수 있습니다'}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* 보유자 팝업 */}
            {owners && (
                <div
                    onClick={() => setOwners(null)}
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(20, 24, 40, 0.45)',
                        zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
                    }}
                >
                    <div onClick={(e) => e.stopPropagation()} style={{
                        background: '#fff', borderRadius: 16, padding: 18, width: 'min(420px, 100%)',
                        maxHeight: '76vh', overflowY: 'auto', boxShadow: '0 16px 48px rgba(0,0,0,0.3)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                            <strong style={{ fontSize: 15 }}>{owners.label}</strong>
                            <button onClick={() => setOwners(null)} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#999' }}>×</button>
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#667eea', marginBottom: 6, textAlign: 'left' }}>
                            보유 {owners.owners.length}명 · 모두 {owners.total}마리
                        </div>
                        {/* 두 마리 이상 가진 사람은 이름 뒤에 마리 수를 적는다 */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                            {owners.owners.length === 0
                                ? <span style={{ fontSize: 12, color: '#999' }}>아직 없습니다</span>
                                : owners.owners.map((o) => (
                                    <span key={o.uuid} style={chip('#667eea')}>
                                        {o.nickname}{(o.n || 1) > 1 ? ` ×${o.n}` : ''}
                                    </span>
                                ))}
                        </div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#aaa', marginBottom: 6, textAlign: 'left' }}>
                            미보유 {owners.missing.length}명
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {owners.missing.map((o) => <span key={o.uuid} style={chip('#bbb')}>{o.nickname}</span>)}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/** 작은 알약 단추 */
function pill(active: boolean, isMobile: boolean): React.CSSProperties {
    return {
        padding: isMobile ? '4px 9px' : '5px 12px',
        borderRadius: 999,
        border: active ? '1px solid #667eea' : '1px solid rgba(102, 126, 234, 0.25)',
        background: active ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff',
        color: active ? '#fff' : '#667eea',
        fontSize: isMobile ? 11 : 12,
        fontWeight: 700,
        cursor: 'pointer',
        whiteSpace: 'nowrap'
    };
}

function chip(color: string): React.CSSProperties {
    return {
        padding: '3px 9px',
        borderRadius: 999,
        background: color === '#667eea' ? 'rgba(102, 126, 234, 0.12)' : 'rgba(0,0,0,0.05)',
        color,
        fontSize: 12,
        fontWeight: 700
    };
}

export default Roster;
